import { useEffect, useRef, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { Instagram, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";

const INSTAGRAM_URL = "https://www.instagram.com/shoresightmarine";
const WIDGET_CLASS_REGEX = /elfsight-app-[a-f0-9-]+/;

/** Extract the Elfsight widget class from raw embed HTML stored in the DB. */
function extractWidgetClass(embed: string): string | null {
  const match = embed.match(WIDGET_CLASS_REGEX);
  return match ? match[0] : null;
}

export const InstagramFeed = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);
  const [isNearViewport, setIsNearViewport] = useState(false);

  // Defer data fetching until section is near the viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings-instagram"],
    enabled: isNearViewport,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("settings")
        .select("elfsight_instagram_embed")
        .eq("id", 1)
        .maybeSingle();

      if (error) {
        console.warn("InstagramFeed: failed to load settings", error.message);
        return null;
      }
      return data;
    },
    retry: false,
  });

  const widgetClass = useMemo(
    () =>
      settings?.elfsight_instagram_embed
        ? extractWidgetClass(settings.elfsight_instagram_embed)
        : null,
    [settings?.elfsight_instagram_embed]
  );

  // Load Elfsight platform script once we have a valid widget class
  useEffect(() => {
    if (!widgetClass || scriptLoadedRef.current) return;

    const existingScript = document.querySelector(
      'script[src="https://elfsightcdn.com/platform.js"]'
    );

    if (!existingScript) {
      const script = document.createElement("script");
      script.src = "https://elfsightcdn.com/platform.js";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    scriptLoadedRef.current = true;
  }, [widgetClass]);

  // Reinitialize Elfsight after widget div mounts (handles late-injection)
  useEffect(() => {
    if (!widgetClass || !widgetRef.current) return;

    // Give the platform script a moment to load, then reinit
    const timer = setTimeout(() => {
      try {
        (window as any).elfsight?.reinit?.();
      } catch {
        // Elfsight may not be ready yet — widget will self-init via MutationObserver
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [widgetClass]);

  // Loading / pre-viewport skeleton
  if (isLoading || !isNearViewport) {
    return (
      <section ref={sectionRef} className="py-16 md:py-20 bg-accent/30">
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <div className="animate-pulse bg-muted h-64 w-full max-w-4xl rounded-lg" />
          </div>
        </div>
      </section>
    );
  }

  // No embed configured or malformed — gracefully hide
  if (!widgetClass) {
    return null;
  }

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-accent/30 overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 mb-4 group"
            aria-label="Follow ShoreSight Marine on Instagram"
          >
            <Instagram className="h-8 w-8 text-[hsl(330,70%,50%)] group-hover:scale-110 transition-transform" />
            <h2 className="text-3xl md:text-4xl font-bold text-foreground group-hover:text-azure-500 transition-colors">
              Follow Us on Instagram
            </h2>
          </a>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See our latest projects and behind-the-scenes work
          </p>
        </motion.div>

        {/* Elfsight widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-6xl mx-auto"
        >
          <div
            ref={widgetRef}
            className={widgetClass}
            data-elfsight-app-lazy
          />
        </motion.div>

        {/* CTA button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex justify-center mt-10"
        >
          <Button
            size="lg"
            asChild
            className="bg-azure-500 hover:bg-azure-hover text-white"
          >
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <Instagram className="h-5 w-5" />
              Follow @shoresightmarine on Instagram
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
